import React, { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import { useEmployer } from "@/context/EmployerContext";
import AdminLogo from "@/components/AdminLogo";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Loader2, UserPlus } from "lucide-react";

export default function EmployerRegister() {
  const { t } = useTranslation();
  const { register } = useEmployer();
  const navigate = useNavigate();
  const [form, setForm] = useState({ name: "", email: "", phone: "", company: "", password: "", password2: "", notes: "" });
  const [loading, setLoading] = useState(false);

  const set = (k) => (e) => setForm((p) => ({ ...p, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    if (form.password !== form.password2) { toast.error(t("emp.pw_mismatch", "Passwords do not match")); return; }
    if (form.password.length < 8) { toast.error(t("emp.pw_short", "Password must be at least 8 characters")); return; }
    setLoading(true);
    try {
      await register({
        email: form.email, password: form.password, name: form.name,
        phone: form.phone, company: form.company, notes: form.notes,
      });
      toast.success(t("emp.registered", "Account created — please contact admin on WhatsApp"));
      navigate("/employer/pending", { state: { email: form.email } });
    } catch (err) {
      toast.error(err.response?.data?.detail || t("emp.reg_failed", "Registration failed"));
    } finally { setLoading(false); }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-zinc-950 grain relative overflow-hidden py-16">
      <div className="absolute -top-40 -right-40 w-[520px] h-[520px] rounded-full blur-3xl opacity-25 pointer-events-none"
           style={{ background: "radial-gradient(circle, #b8860b 0%, transparent 70%)" }} />
      <div className="w-full max-w-xl p-9 bg-zinc-900/85 backdrop-blur-2xl rounded-[28px] border border-zinc-800/80 shadow-2xl relative">
        <div className="flex items-center gap-3 mb-6">
          <AdminLogo size={44} variant="light" />
          <div className="text-[10px] tracking-[0.24em] uppercase text-zinc-500 font-medium">Employer Portal</div>
        </div>
        <div className="text-[11px] tracking-[0.22em] uppercase text-[#d4a017] font-semibold mb-2">
          {t("emp.reg_overline", "Create Employer Account")}
        </div>
        <h1 className="font-serif text-white text-3xl mb-3 tracking-tight">
          {t("emp.reg_title", "Register to access candidates")}
        </h1>
        <p className="text-sm text-zinc-400 mb-6 leading-relaxed">
          {t("emp.reg_subtitle", "After registering, chat our admin on WhatsApp for identity verification. Once verified you can browse the full catalog and book interviews.")}
        </p>
        <form onSubmit={submit} className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <Label className="text-zinc-300 text-xs uppercase tracking-wider font-medium">{t("emp.full_name", "Full Name")}</Label>
              <Input value={form.name} onChange={set("name")} className="mt-1.5 bg-zinc-800/70 border-zinc-700 text-white focus:border-[#d4a017]" data-testid="reg-name" required />
            </div>
            <div>
              <Label className="text-zinc-300 text-xs uppercase tracking-wider font-medium">{t("emp.company", "Company")}</Label>
              <Input value={form.company} onChange={set("company")} className="mt-1.5 bg-zinc-800/70 border-zinc-700 text-white focus:border-[#d4a017]" data-testid="reg-company" />
            </div>
            <div>
              <Label className="text-zinc-300 text-xs uppercase tracking-wider font-medium">{t("emp.email", "Email")}</Label>
              <Input type="email" value={form.email} onChange={set("email")} className="mt-1.5 bg-zinc-800/70 border-zinc-700 text-white focus:border-[#d4a017]" data-testid="reg-email" required />
            </div>
            <div>
              <Label className="text-zinc-300 text-xs uppercase tracking-wider font-medium">{t("emp.whatsapp", "WhatsApp Number")}</Label>
              <Input value={form.phone} onChange={set("phone")} className="mt-1.5 bg-zinc-800/70 border-zinc-700 text-white focus:border-[#d4a017]" placeholder="+62 812 xxxx" data-testid="reg-phone" required />
            </div>
            <div>
              <Label className="text-zinc-300 text-xs uppercase tracking-wider font-medium">{t("emp.password", "Password")}</Label>
              <Input type="password" value={form.password} onChange={set("password")} className="mt-1.5 bg-zinc-800/70 border-zinc-700 text-white focus:border-[#d4a017]" data-testid="reg-pw" required />
            </div>
            <div>
              <Label className="text-zinc-300 text-xs uppercase tracking-wider font-medium">{t("emp.confirm_password", "Confirm Password")}</Label>
              <Input type="password" value={form.password2} onChange={set("password2")} className="mt-1.5 bg-zinc-800/70 border-zinc-700 text-white focus:border-[#d4a017]" data-testid="reg-pw2" required />
            </div>
          </div>
          <div>
            <Label className="text-zinc-300 text-xs uppercase tracking-wider font-medium">{t("emp.notes_label", "Notes (optional)")}</Label>
            <Textarea rows={2} value={form.notes} onChange={set("notes")} className="mt-1.5 bg-zinc-800/70 border-zinc-700 text-white focus:border-[#d4a017]" placeholder={t("emp.notes_ph", "What kind of candidate are you looking for?")} data-testid="reg-notes" />
          </div>
          <button type="submit" disabled={loading} className="btn-gold w-full disabled:opacity-60 flex items-center justify-center gap-2" data-testid="reg-submit">
            {loading ? <Loader2 size={16} className="animate-spin" /> : <UserPlus size={14} />}
            {loading ? t("emp.registering", "Creating account…") : t("emp.create_account", "Create account")}
          </button>
        </form>
        <div className="mt-6 text-sm text-zinc-400 text-center">
          {t("emp.have_account", "Already have an account?")}{" "}
          <Link to="/employer/login" className="text-[#d4a017] hover:underline" data-testid="reg-goto-login">
            {t("emp.sign_in", "Sign in")}
          </Link>
        </div>
      </div>
    </div>
  );
}
